import { createdAtDayKey, formatWeekRangeLabel, mondayWeekKey } from './connectionDashboard';

export type CampaignTrendBucketMode = '15min' | 'hourly' | '6hour' | 'daily' | 'weekly';

export type CampaignTrendPoint = {
  label: string;
  periodStart: string;
  /** Emails marked sent in this bucket. */
  sentCount: number;
  /** Open events in this bucket. */
  openCount: number;
  /** Click events in this bucket. */
  clickCount: number;
  isWeekStart: boolean;
};

export type CampaignSendTimestamp = {
  sent_at: string;
};

export type CampaignEngagementEvent = {
  event_type: string;
  occurred_at: string;
  scheduled_email_id?: string | null;
};

export type CampaignTrendSeries = {
  points: CampaignTrendPoint[];
  bucketMinutes: number;
  bucketMode: CampaignTrendBucketMode;
};

export type CampaignTrendBucketSelection = CampaignTrendBucketMode | 'auto';

export const CAMPAIGN_TREND_BUCKET_OPTIONS: ReadonlyArray<{
  mode: CampaignTrendBucketMode;
  minutes: number;
  label: string;
}> = [
  { mode: '15min', minutes: 15, label: '15 min' },
  { mode: 'hourly', minutes: 60, label: 'Hourly' },
  { mode: '6hour', minutes: 360, label: '6 hours' },
  { mode: 'daily', minutes: 1440, label: 'Daily' },
  { mode: 'weekly', minutes: 10_080, label: 'Weekly' },
];

export function bucketMinutesForMode(mode: CampaignTrendBucketMode): number {
  return CAMPAIGN_TREND_BUCKET_OPTIONS.find((o) => o.mode === mode)?.minutes ?? 15;
}

const MAX_BUCKETS = 96;
const BUCKET_MINUTES_OPTIONS = [15, 60, 360, 1440, 10_080] as const;

function parseTimestamp(value: string): Date | null {
  if (!value?.trim()) return null;
  const normalized = value.includes('T') ? value : value.replace(' ', 'T');
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

function startOfLocalDay(d: Date): Date {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function startOfLocalHour(d: Date): Date {
  const copy = new Date(d);
  copy.setMinutes(0, 0, 0);
  return copy;
}

function startOfLocalMinuteInterval(d: Date, intervalMinutes: number): Date {
  const copy = startOfLocalHour(d);
  const flooredMinutes = Math.floor(d.getMinutes() / intervalMinutes) * intervalMinutes;
  copy.setMinutes(flooredMinutes);
  return copy;
}

function localDayKeyFromDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatDayLabel(isoDate: string): string {
  const d = new Date(`${isoDate}T12:00:00`);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function isMonday(isoDate: string): boolean {
  return new Date(`${isoDate}T12:00:00`).getDay() === 1;
}

function parseLocalPeriodStart(periodStart: string): Date | null {
  const hourMatch = periodStart.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2})$/);
  if (hourMatch) {
    const [, y, m, d, hour] = hourMatch;
    return new Date(Number(y), Number(m) - 1, Number(d), Number(hour), 0, 0, 0);
  }

  const minuteMatch = periodStart.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (minuteMatch) {
    const [, y, m, d, hour, minute] = minuteMatch;
    return new Date(Number(y), Number(m) - 1, Number(d), Number(hour), Number(minute), 0, 0);
  }

  const dayMatch = periodStart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dayMatch) {
    const [, y, m, d] = dayMatch;
    return new Date(Number(y), Number(m) - 1, Number(d), 12, 0, 0, 0);
  }

  return null;
}

function localBucketKeyFromDate(d: Date, bucketMinutes: number): string {
  if (bucketMinutes >= 10_080) {
    return mondayWeekKey(localDayKeyFromDate(d));
  }
  if (bucketMinutes >= 1440) {
    return localDayKeyFromDate(startOfLocalDay(d));
  }
  if (bucketMinutes >= 60) {
    const bucket = startOfLocalHour(d);
    if (bucketMinutes === 360) {
      const flooredHour = Math.floor(bucket.getHours() / 6) * 6;
      bucket.setHours(flooredHour);
    }
    const y = bucket.getFullYear();
    const m = String(bucket.getMonth() + 1).padStart(2, '0');
    const day = String(bucket.getDate()).padStart(2, '0');
    const hour = String(bucket.getHours()).padStart(2, '0');
    return `${y}-${m}-${day}T${hour}`;
  }

  const bucket = startOfLocalMinuteInterval(d, bucketMinutes);
  const y = bucket.getFullYear();
  const m = String(bucket.getMonth() + 1).padStart(2, '0');
  const day = String(bucket.getDate()).padStart(2, '0');
  const hour = String(bucket.getHours()).padStart(2, '0');
  const minute = String(bucket.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${day}T${hour}:${minute}`;
}

function bucketStartFromKey(key: string): Date | null {
  return parseLocalPeriodStart(key);
}

function bucketEndFromStart(start: Date, bucketMinutes: number): Date {
  const end = new Date(start);
  end.setMinutes(end.getMinutes() + bucketMinutes);
  end.setMilliseconds(end.getMilliseconds() - 1);
  return end;
}

function formatBucketLabel(key: string, bucketMode: CampaignTrendBucketMode): string {
  const at = parseLocalPeriodStart(key);
  if (!at) return key;

  if (bucketMode === 'weekly') return formatWeekRangeLabel(key);
  if (bucketMode === 'daily') {
    return at.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
  if (bucketMode === '6hour' || bucketMode === 'hourly') {
    return at.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
    });
  }
  return at.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

export function pickCampaignBucketMinutes(spanMs: number): number {
  const spanMinutes = Math.max(1, spanMs / (60 * 1000));
  for (const mins of BUCKET_MINUTES_OPTIONS) {
    if (spanMinutes / mins <= MAX_BUCKETS) return mins;
  }
  return 10_080;
}

export function getCampaignTrendBucketMode(bucketMinutes: number): CampaignTrendBucketMode {
  if (bucketMinutes >= 10_080) return 'weekly';
  if (bucketMinutes >= 1440) return 'daily';
  if (bucketMinutes >= 360) return '6hour';
  if (bucketMinutes >= 60) return 'hourly';
  return '15min';
}

export function getCampaignTrendUnitLabel(bucketMode: CampaignTrendBucketMode): string {
  switch (bucketMode) {
    case '15min':
      return '15 min';
    case 'hourly':
      return 'Hourly';
    case '6hour':
      return '6 hours';
    case 'daily':
      return 'Daily';
    case 'weekly':
      return 'Weekly';
  }
}

export function formatCampaignTrendAxisLabel(
  periodStart: string,
  bucketMode: CampaignTrendBucketMode,
): string {
  const at = parseLocalPeriodStart(periodStart);
  if (!at) return periodStart;

  if (bucketMode === 'weekly') return formatWeekRangeLabel(periodStart);
  if (bucketMode === 'daily') {
    return at.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
  if (bucketMode === '6hour' || bucketMode === 'hourly') {
    return at.toLocaleTimeString(undefined, { hour: 'numeric' });
  }
  return at.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

export function formatCampaignTrendTooltip(
  periodStart: string,
  bucketMode: CampaignTrendBucketMode,
): string {
  const at = parseLocalPeriodStart(periodStart);
  if (!at) return periodStart;

  if (bucketMode === 'weekly') {
    return formatWeekRangeLabel(periodStart);
  }

  return at.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: bucketMode === 'daily' ? undefined : 'numeric',
    minute: bucketMode === '15min' ? '2-digit' : undefined,
  });
}

function buildBucketKeys(anchor: Date, end: Date, bucketMinutes: number): string[] {
  const keys: string[] = [];
  const intervalMs = bucketMinutes * 60 * 1000;
  let cursor = bucketStartFromKey(localBucketKeyFromDate(anchor, bucketMinutes));
  if (!cursor) return keys;

  const endKey = localBucketKeyFromDate(end, bucketMinutes);
  while (true) {
    const key = localBucketKeyFromDate(cursor, bucketMinutes);
    keys.push(key);
    if (key >= endKey) break;
    cursor = new Date(cursor.getTime() + intervalMs);
  }

  return keys;
}

/** Activity per time bucket from first send through now. */
export function buildCampaignEngagementTrend(
  anchorIso: string,
  sends: CampaignSendTimestamp[],
  events: CampaignEngagementEvent[],
  options?: { now?: Date; bucketMinutes?: number },
): CampaignTrendSeries {
  const anchor = parseTimestamp(anchorIso);
  if (!anchor) {
    return { points: [], bucketMinutes: 15, bucketMode: '15min' };
  }

  const now = options?.now ?? new Date();
  const spanMs = Math.max(0, now.getTime() - anchor.getTime());
  const bucketMinutes = options?.bucketMinutes ?? pickCampaignBucketMinutes(spanMs);
  const bucketMode = getCampaignTrendBucketMode(bucketMinutes);
  const keys = buildBucketKeys(anchor, now, bucketMinutes);
  if (keys.length === 0) {
    return { points: [], bucketMinutes, bucketMode };
  }

  const sendTimes = sends
    .map((s) => parseTimestamp(s.sent_at))
    .filter((d): d is Date => d != null)
    .sort((a, b) => a.getTime() - b.getTime());

  const sortedEvents = [...events].sort((a, b) => a.occurred_at.localeCompare(b.occurred_at));

  let sendIdx = 0;
  let eventIdx = 0;

  const points: CampaignTrendPoint[] = keys.map((key) => {
    const bucketStart = bucketStartFromKey(key);
    if (!bucketStart) {
      return {
        label: formatBucketLabel(key, bucketMode),
        periodStart: key,
        sentCount: 0,
        openCount: 0,
        clickCount: 0,
        isWeekStart: false,
      };
    }
    const bucketEnd = bucketEndFromStart(bucketStart, bucketMinutes);

    while (sendIdx < sendTimes.length && sendTimes[sendIdx]! < bucketStart) {
      sendIdx += 1;
    }

    let sentCount = 0;
    let scanSend = sendIdx;
    while (scanSend < sendTimes.length && sendTimes[scanSend]! <= bucketEnd) {
      sentCount += 1;
      scanSend += 1;
    }
    sendIdx = scanSend;

    while (eventIdx < sortedEvents.length) {
      const eventAt = parseTimestamp(sortedEvents[eventIdx]!.occurred_at);
      if (!eventAt || eventAt >= bucketStart) break;
      eventIdx += 1;
    }

    let openCount = 0;
    let clickCount = 0;
    while (eventIdx < sortedEvents.length) {
      const event = sortedEvents[eventIdx]!;
      const eventAt = parseTimestamp(event.occurred_at);
      if (!eventAt || eventAt > bucketEnd) break;
      if (event.event_type === 'open') openCount += 1;
      else if (event.event_type === 'click') clickCount += 1;
      eventIdx += 1;
    }

    const dayKey = createdAtDayKey(key) || key.slice(0, 10);

    return {
      label: formatBucketLabel(key, bucketMode),
      periodStart: key,
      sentCount,
      openCount,
      clickCount,
      isWeekStart: bucketMode === 'weekly' || isMonday(dayKey),
    };
  });

  return { points, bucketMinutes, bucketMode };
}

/** @deprecated Use buildCampaignEngagementTrend */
export function buildCampaignClickTrend(
  events: { event_type: string; occurred_at: string }[],
  anchorIso: string,
): CampaignTrendPoint[] {
  return buildCampaignEngagementTrend(anchorIso, [], events).points;
}
